import React, {
  ClipboardEvent,
  KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import { OverlayLoading, Spinner } from "@via/components/loading/loading.tsx";
import FoundryConnectLogo from "@via/components/foundry-connect-logo.tsx";
import LoginLink from "@via/components/login-link.tsx";

interface EmailConfirmationProps {
  email: string;
  onVerify: (code: string) => void;
  onResend: () => Promise<void>;
  isError: boolean;
  isLoading: boolean;
}

const EmailConfirmation: React.FC<EmailConfirmationProps> = ({
  email,
  onVerify,
  onResend,
  isError,
  isLoading,
}) => {
  const [otp, setOtp] = useState<string[]>(new Array(6).fill(""));
  const [isResending, setIsResending] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  useEffect(() => {
    if (isError) {
      const timer = setTimeout(() => {
        setOtp(new Array(6).fill(""));
        if (inputRefs.current[0]) {
          inputRefs.current[0].focus();
        }
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [isError]);

  const handleChange = (element: HTMLInputElement, index: number) => {
    const value = element.value;
    if (!/^[0-9]?$/.test(value)) {
      element.value = otp[index] || "";
      return;
    }

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5 && inputRefs.current[index + 1]) {
      inputRefs.current[index + 1]!.focus();
    }

    const filledOtp = newOtp.filter(digit => digit !== "");
    if (filledOtp.length === 6) {
      onVerify(filledOtp.join(""));
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const newOtp = [...otp];
      if (otp[index] !== "") {
        newOtp[index] = "";
        setOtp(newOtp);
      } else if (index > 0 && inputRefs.current[index - 1]) {
        newOtp[index - 1] = "";
        setOtp(newOtp);
        inputRefs.current[index - 1]!.focus();
      }
    } else if (
      e.key === "ArrowLeft" &&
      index > 0 &&
      inputRefs.current[index - 1]
    ) {
      e.preventDefault();
      inputRefs.current[index - 1]!.focus();
    } else if (
      e.key === "ArrowRight" &&
      index < 5 &&
      inputRefs.current[index + 1]
    ) {
      e.preventDefault();
      inputRefs.current[index + 1]!.focus();
    }
  };

  const handlePaste = (
    e: ClipboardEvent<HTMLInputElement>,
    startIndex: number
  ) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text").replace(/[^0-9]/g, "");
    const newOtp = [...otp];
    let charsPasted = 0;

    for (let i = 0; i < pasteData.length && startIndex + i < 6; i++) {
      newOtp[startIndex + i] = pasteData[i];
      charsPasted++;
    }
    setOtp(newOtp);

    const nextFocusIndex = Math.min(startIndex + charsPasted, 5);
    if (inputRefs.current[nextFocusIndex]) {
      inputRefs.current[nextFocusIndex]!.focus();
    }

    if (newOtp.every(digit => digit !== "")) {
      onVerify(newOtp.join(""));
    }
  };

  const handleResendCode = async () => {
    setIsResending(true);
    try {
      await onResend();
      setOtp(new Array(6).fill(""));
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }
    } catch (error) {
      console.error("Failed to resend code", error);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="p-6 sm:p-8 w-full max-w-md text-center">
      {isLoading ? <OverlayLoading /> : null}

      <FoundryConnectLogo />

      <h1 className="mt-10 text-2xl sm:text-3xl font-semibold text-gray-800 mb-2">
        Verify your email
      </h1>
      <p className="text-sm text-gray-600 mb-6 sm:mb-8">
        We&#39;ve sent a six-digit code to{" "}
        <span className="font-medium text-gray-700">{email}</span>
      </p>

      <div
        className="flex items-center justify-center space-x-1 sm:space-x-2 mb-6 sm:mb-8"
        onPaste={e =>
          handlePaste(e as unknown as ClipboardEvent<HTMLInputElement>, 0)
        }>
        {otp.map((digit, index) => (
          <React.Fragment key={index}>
            <input
              ref={el => {
                if (el) inputRefs.current[index] = el;
              }}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={1}
              className="w-12 h-14 sm:w-14 sm:h-16 text-center text-2xl sm:text-3xl font-medium border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all shadow-sm bg-white"
              value={digit}
              onChange={e => handleChange(e.target as HTMLInputElement, index)}
              onKeyDown={e =>
                handleKeyDown(e as KeyboardEvent<HTMLInputElement>, index)
              }
              onFocus={e => (e.target as HTMLInputElement).select()}
            />
            {index === 2 && (
              <span className="mx-1 sm:mx-2 text-3xl font-light text-gray-400 select-none">
                -
              </span>
            )}
          </React.Fragment>
        ))}
      </div>

      <p className="text-xs sm:text-sm text-gray-500 mb-2">
        Didn&#39;t receive the code?
      </p>
      <button
        onClick={handleResendCode}
        disabled={isResending}
        className="text-indigo-600 hover:text-indigo-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors rounded-md px-2 py-1">
        <Spinner loading={isResending}>
          {isResending ? "Sending..." : "Resend Code"}
        </Spinner>
      </button>
      <LoginLink />
    </div>
  );
};

export default EmailConfirmation;
